import { ConnectionTypeEnum, State } from "src/components/Premises/types";
import {
  mapKeyToConnectionTypeLabel,
  getAggregatedConnectionTypes,
} from "../helper";
import { normaliseDate } from "src/api/apiHelper";
import {
  EIC,
  FuelTypeForLifeSupport,
  PostalAddress,
  EICFormData,
  Account,
  EicRequestsMetaUnderSamePremise,
  EicRequestMeta,
} from "./types";
import { getFormattedAddress } from "../../Premises/helpers";
import { poBoxTypes } from "../FormComponents/AddressConstants";

const ELECTRICITY_TYPES = new Set([
  ConnectionTypeEnum.PERMANENT,
  ConnectionTypeEnum.TEMPORARY,
  ConnectionTypeEnum.TEMPPERM,
]);

const GAS_TYPES = new Set([
  ConnectionTypeEnum.METER_FIX,
  ConnectionTypeEnum.SERVICE_CONNECTION_AND_METER_FIX,
]);

const PO_BOX_TYPES_WITHOUT_NUMBER = new Set(["carePo", "CPA", "CMA"]);

export const getIsElectricityAndGas = (
  connectionTypes: ConnectionTypeEnum[]
): boolean => {
  if (connectionTypes.length < 2) return false;

  let hasElectricity = false;
  let hasGas = false;

  for (const type of connectionTypes) {
    if (ELECTRICITY_TYPES.has(type)) hasElectricity = true;
    if (GAS_TYPES.has(type)) hasGas = true;

    // Early return if both found
    if (hasElectricity && hasGas) return true;
  }

  return hasElectricity && hasGas;
};

export const getHasElectricity = (
  connectionTypes: ConnectionTypeEnum[]
): boolean => {
  return connectionTypes.some((type) => ELECTRICITY_TYPES.has(type));
};

export const shouldHidePoBoxNumber = (poBoxType: string): boolean => {
  return PO_BOX_TYPES_WITHOUT_NUMBER.has(poBoxType);
};

export const getConnectionTypeLabels = (
  connectionTypes: ConnectionTypeEnum[],
  elecInGeneral = true
): string => {
  return connectionTypes
    .map((type) => mapKeyToConnectionTypeLabel(type, elecInGeneral))
    .join(", ");
};

export const getIsGasLineOnly = (
  connectionTypes: ConnectionTypeEnum[]
): boolean => {
  return (
    connectionTypes.length === 1 &&
    connectionTypes[0] === ConnectionTypeEnum.SERVICE_CONNECTION
  );
};

export const getIsVIC = (address: string): boolean => {
  return address.toLowerCase().includes("vic");
};

// Mapper function to transform EIC form data to API schema
export const mapFormDataToAPI = (
  formData: EICFormData,
  recaptchaToken?: string
): Partial<EIC> => {
  const getFuelTypeForLifeSupport = (): FuelTypeForLifeSupport | undefined => {
    if (formData.lifeSupport === "yes") {
      //single connection, either electricity or gas
      if (formData.whichEnergy === "") {
        return getAggregatedConnectionTypes(
          formData.connectionTypes,
          "BOTH"
        )[0] as FuelTypeForLifeSupport.ELECTRICITY | FuelTypeForLifeSupport.GAS;
      } else {
        // multiple connections, either electricity or gas
        switch (formData.whichEnergy) {
          case "electricity":
            return FuelTypeForLifeSupport.ELECTRICITY;
          case "natural-gas":
            return FuelTypeForLifeSupport.GAS;
          case "both":
            return FuelTypeForLifeSupport.BOTH;
          default:
            return undefined;
        }
      }
    }
    return undefined;
  };

  const getPostalAddressSameAsPremise = (): boolean => {
    return formData.postalAddressType === "premiseAddress";
  };

  const buildPostalAddress = (): PostalAddress | undefined => {
    if (formData.postalAddressType === "premiseAddress") {
      return undefined; // Use premise address
    }

    if (formData.postalAddressType === "postalAddress") {
      return {
        unit_type: formData.postalPremiseType || "",
        unit_number: formData.postalUnitNo || "",
        floor_type: "", // Not captured in form
        floor_number: formData.postalLevelNo || "",
        building_name: "", // Not captured in form
        location_descriptor: "", // Not captured in form
        street_number: formData.postalStreetNo || "",
        street_number_suffix: "", // Not captured in form
        lot_number: "", // Not captured in form
        street_name: formData.postalStreetName || "",
        street_type: formData.postalStreetType || "",
        street_suffix: formData.postalSuffix || "",
        postal_delivery_type: "", // Not captured in form
        postal_delivery_number_prefix: "", // Not captured in form
        postal_delivery_number_value: "", // Not captured in form
        postal_delivery_number_suffix: "", // Not captured in form
        suburb: formData.postalSuburb || "",
        state: formData.postalState || "",
        postcode: formData.postalPostcode || "",
      };
    }

    if (formData.postalAddressType === "poBox") {
      return {
        unit_type: "", // Not applicable for PO Box
        unit_number: "", // Not applicable for PO Box
        floor_type: "", // Not applicable for PO Box
        floor_number: "", // Not applicable for PO Box
        building_name: "", // Not applicable for PO Box
        location_descriptor: "", // Not applicable for PO Box
        street_number: "", // Not applicable for PO Box
        street_number_suffix: "", // Not applicable for PO Box
        lot_number: "", // Not applicable for PO Box
        street_name: "", // Not applicable for PO Box
        street_type: "", // Not applicable for PO Box
        street_suffix: "", // Not applicable for PO Box
        postal_delivery_type: formData.poBoxType || "",
        postal_delivery_number_prefix: "", // Not captured in form
        postal_delivery_number_value: formData.poBoxNo || "",
        postal_delivery_number_suffix: "", // Not captured in form
        suburb: formData.poBoxSuburb || "",
        state: formData.poBoxState || "",
        postcode: formData.poBoxPostcode || "",
      };
    }

    return undefined;
  };

  const apiData: Partial<EIC> = {
    draft_id: formData.referenceNumber,
    reCaptcha: recaptchaToken,
    postcode: formData.postcode,
    account_type: formData.accountType,
    first_name: formData.firstName,
    last_name: formData.lastName,
    date_of_birth: normaliseDate(formData.dateOfBirth, "") || undefined,
    email: formData.email,
    phone: formData.phone,
    send_communications_by: formData.billDeliveryMethod,
    postal_address_same_as_premise: getPostalAddressSameAsPremise(),
    premise_details: buildPostalAddress(),
    fuel_type_for_life_support: getFuelTypeForLifeSupport(),
  };

  // Add business-specific fields
  if (formData.accountType === "business") {
    apiData.company_name = formData.companyName;
    apiData.abn = formData.abn;
    apiData.primary_contact_firstName = formData.firstName;
    apiData.primary_contact_lastName = formData.lastName;
    apiData.primary_contact_phone = formData.phone;
    apiData.primary_contactEmail = formData.email;
  }

  // Add secondary account holder if present
  if (formData.addSecondaryAccountHolder) {
    apiData.secondary_account = {
      first_name: formData.secondaryFirstName,
      last_name: formData.secondaryLastName,
      date_of_birth: normaliseDate(formData.secondaryDateOfBirth, "") || "",
      phone_number: formData.secondaryPhone,
      email: formData.secondaryEmail,
      add_second_name_to_bill: formData.addSecondaryToBills,
    };
  }

  return apiData;
};

// Function to map account details from API response to form values
export const mapAccountDetailsToFormValues = (accountDetails: Account) => {
  // Determine postal address type based on postal address data
  let postalAddressType: "premiseAddress" | "postalAddress" | "poBox" =
    "postalAddress";

  if (accountDetails.postal_address_same_as_premise) {
    postalAddressType = "premiseAddress";
  } else if (accountDetails.postal_address?.postal_delivery_type) {
    // If postal_delivery_type is present, it's likely a PO Box
    postalAddressType = "poBox";
  }

  console.log("000accountDetails", accountDetails);
  const baseValues = {
    accountType: accountDetails.company_name ? "business" : "personal",
    firstName: accountDetails.first_name || "",
    lastName: accountDetails.last_name || "",
    dateOfBirth: accountDetails.date_of_birth
      ? new Date(accountDetails.date_of_birth)
      : null,
    phone: accountDetails.phone_number || "",
    email: accountDetails.email || "",
    companyName: accountDetails.company_name || "",
    abn: accountDetails.company_number || "",
    addSecondaryAccountHolder: Boolean(accountDetails.secondary_account),
    secondaryFirstName: accountDetails.secondary_account?.first_name || "",
    secondaryLastName: accountDetails.secondary_account?.last_name || "",
    secondaryDateOfBirth: accountDetails.secondary_account?.date_of_birth
      ? new Date(accountDetails.secondary_account.date_of_birth)
      : null,
    secondaryPhone: accountDetails.secondary_account?.phone_number || "",
    secondaryEmail: accountDetails.secondary_account?.email || "",
    addSecondaryToBills:
      accountDetails.secondary_account?.add_second_name_to_bill || false,
    // Postal address mapping
    postalAddressType,
  };

  // Map postal address fields based on type
  if (postalAddressType === "poBox") {
    return {
      ...baseValues,
      // PO Box specific fields
      poBoxType: accountDetails.postal_address?.postal_delivery_type || "",
      poBoxNo:
        accountDetails.postal_address?.postal_delivery_number_value || "",
      poBoxSuburb: accountDetails.postal_address?.suburb || "",
      poBoxState: accountDetails.postal_address?.state || "",
      poBoxPostcode: accountDetails.postal_address?.postcode || "",
      // Clear regular postal address fields for PO Box
      postalUnitNo: "",
      postalPremiseType: "",
      postalLevelNo: "",
      postalStreetNo: "",
      postalStreetName: "",
      postalStreetType: "",
      postalSuffix: "",
      postalSuburb: "",
      postalState: "",
      postalPostcode: "",
    };
  } else {
    return {
      ...baseValues,
      // Regular postal address fields
      postalUnitNo: accountDetails.postal_address?.unit_number || "",
      postalPremiseType: accountDetails.postal_address?.unit_type || "",
      postalLevelNo: accountDetails.postal_address?.floor_number || "",
      postalStreetNo: accountDetails.postal_address?.street_number || "",
      postalStreetName: accountDetails.postal_address?.street_name || "",
      postalStreetType: accountDetails.postal_address?.street_type || "",
      postalSuffix: accountDetails.postal_address?.street_suffix || "",
      postalSuburb: accountDetails.postal_address?.suburb || "",
      postalState: accountDetails.postal_address?.state || "",
      postalPostcode: accountDetails.postal_address?.postcode || "",
      // Clear PO Box fields for regular address
      poBoxType: "",
      poBoxNo: "",
      poBoxSuburb: "",
      poBoxState: "",
      poBoxPostcode: "",
    };
  }
};

export const capitalizeFirstLetter = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1);
};

// Function to analyze EIC request statuses in a single loop
export const analyzeEicRequestStatuses = (
  eicMeta: EicRequestsMetaUnderSamePremise
) => {
  let acceptedCount = 0;
  let firstAcceptedRequest: EicRequestMeta | null = null;

  for (const item of eicMeta) {
    if (item.eic_status === "EIC_ACCEPTED") {
      acceptedCount++;
      if (!firstAcceptedRequest) {
        firstAcceptedRequest = item;
      }
    }
  }

  const allAccepted = acceptedCount === eicMeta.length;
  const hasMixedStatuses = acceptedCount > 0 && acceptedCount < eicMeta.length;

  return {
    acceptedCount,
    allAccepted,
    hasMixedStatuses,
    firstAcceptedRequest,
  };
};

// Function to get postal address based on postal address type
export const getPostalAddress = (values: EICFormData): string => {
  if (values.postalAddressType === "premiseAddress") {
    return values.address;
  } else if (values.postalAddressType === "postalAddress") {
    // Create address object structure that getFormattedAddress expects
    const addressObj = {
      id: "",
      unit_type: values.postalPremiseType || "",
      unit_number: values.postalUnitNo || "",
      floor_type: "",
      floor_number: values.postalLevelNo || "",
      site_details: {
        id: "",
        street_number: values.postalStreetNo || "",
        street_name: values.postalStreetName || "",
        street_type: values.postalStreetType || "",
        suburb: values.postalSuburb || "",
        state: values.postalState as State,
        postcode: values.postalPostcode || "",
        lot_number: "",
        dp_number: "",
        block_number: "",
        section_number: "",
        building_name: "",
        street_suffix: values.postalSuffix || "",
      },
    };
    return getFormattedAddress(addressObj);
  } else if (values.postalAddressType === "poBox") {
    return `${poBoxTypes.find((p) => p.value === values.poBoxType)?.label} ${
      values.poBoxNo
    }, ${values.poBoxSuburb} ${values.poBoxState} ${values.poBoxPostcode}`;
  }
  return "";
};

// Function to format date in Australian format
export const formatDate = (date: Date | null): string => {
  if (!date) return "";
  return date.toLocaleDateString("en-AU", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

// Function to get life support services string based on form values
export const getLifeSupportServices = (values: EICFormData): string => {
  if (values.lifeSupport === "no") return "No";
  if (values.whichEnergy === "electricity") return "Electricity";
  if (values.whichEnergy === "natural-gas") return "Natural gas";
  if (values.whichEnergy === "both") return "Both";
  return "Yes";
};
